/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  Input,
  Output,
  ViewChild,
  ViewChildren,
  EventEmitter,
  QueryList,
  AfterViewInit,
  OnDestroy,
} from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Settings } from '../../../common/settings';
import {
  Workflow,
  FileType,
  WorkflowService,
  Parameter,
  CloudRepo,
  CloudFileInfo,
  Service,
  OutputType,
  Item,
  CombinedServiceSettings
} from '../../services/workflow.service';
import { NgForm } from '@angular/forms';
import { BasicAlertComponent } from '../alerts/basic-alert.component';
import { SaveAsNewModalComponent } from './save-as-new-modal.component';
import { WorkflowHelperService } from '../../services/workflow-helper.service';
import { XdaStringsService } from '../../services/xda-strings.service';
import { XdaTranslate } from '../../pipes/xda-translate.pipe';
import { ProductPlanTier, UserService } from '../../services/user.service';
import { FileService } from '../../services/file.service';
import { XrxSettingComponent } from '../controls/xrx-setting.component';
import {
  MiddlewareService,
  MWCloudItem,
} from '../../middleware/middleware.service';
import { MiddlewareBrowseModalComponent } from '../../middleware/middleware-browse-modal.component';
import {
  InputSource,
  MergeMode,
  EmailSendCondition,
} from '../../../common/enums';
import { MWSelectedFilesInFolder } from '../../middleware/middleware-browse.component';
import { Subscription } from 'rxjs';
import { XrxMultiFileSelectComponent } from '../controls/xrx-multi-file-select.component';
import { BrowserService } from '../../services/browser.service';
import { AccessListService } from '../../services/access-list.service';

@Component({
  selector: 'workflow-modal-component',
  templateUrl: './workflow-modal.component.html',
  styleUrls: ['./workflow-modal.component.less'],
  standalone: false,
})
export class WorkflowModalComponent implements AfterViewInit, OnDestroy {
  @ViewChild('f', { static: false }) form: NgForm;
  @ViewChildren(XrxMultiFileSelectComponent)
  fileInputsQL: QueryList<XrxMultiFileSelectComponent>;
  @ViewChildren(XrxSettingComponent)
  serviceParamsQL: QueryList<XrxSettingComponent>;

  fileInputs: any[] = [];
  serviceParams: any[] = [];
  onFirstService = false;
  onLastService = false;
  allowSave = true;
  fileInputIsValid = true;
  disablePrintOption = false;
  cloudRepoIsInvalid = false;
  pageRangeSupported = true;

  private _workflow: Workflow;
  private originalWorkflow: Workflow;
  private _cloudRepos = [];
  private currentServiceIndex = 0;

  public currentService: Service;
  licenseErrorSub: Subscription;
  inputSettings = [];
  originalServices: any;
  originalInputSettings: any;
  printSettings: Record<string, Parameter>[] = [];
  steps: Step[] = [];
  serviceSettings: CombinedServiceSettings[] = [];
  originalServiceSettings: CombinedServiceSettings[];
  lastService: any;
  isTranslateWorkflow = false;
  availableFileOutputs: number;
  servicePageIndex = 0;
  step: Step;
  isGoodToGo = true;
  fileFormatNotSupported = false;
  isAdvancedPlan = false;
  mergeMode: any;
  docMergeOptions: any[];
  inputOptions: any[];
  isMergeWorkflow = false;
  isMultipleInputFiles = true;
  inputSourceOptions: any[];
  inputSource: any;
  cloudRepoSub: Subscription;
  errorEmail: string;
  allowedEmailDomains: string;
  showResetServiceSettings = true;

  selectInputRepo: boolean;
  translateFileTypes: any[] = [
    { isChecked: true, value: 'Auto', display: 'SDE_AUTO_SELECT' },
    { isChecked: false, value: 'Pdf', display: 'SDE_PDF1' },
  ];
  selectedTranslateFileType: any = this.translateFileTypes[0];
  specialCharactersInCloudFileName = false;

  @Input() canEditWorkflow = false;
  @Input() set workflow(value: Workflow) {
    // get all services for the workflow
    const services: Service[] = [];
    // deal with catched service objects
    if (typeof value.services[0] === 'string') {
      value.services.forEach((x: any) =>
        services.push(
          this.workflowService.getSqdService(x as string, value.type === 1),
        ),
      );
      value.services = services;
    }

    // Make a deep copy of workflow
    this._workflow = JSON.parse(JSON.stringify(value));
    this.originalWorkflow = JSON.parse(JSON.stringify(value));

    this.onFirstService = true;

    if (this._workflow.services.length == 1) {
      this.onLastService = true;
    }

    this.originalServices = JSON.parse(JSON.stringify(this._workflow.services));
    const firstService: Service = this._workflow.services[0] as Service;
    this.currentService = firstService as Service;
    this.lastService =
      this._workflow.services[this._workflow.services.length - 1];
    this.isTranslateWorkflow = this.lastService.name
      .toLowerCase()
      .includes('translat');
    this.availableFileOutputs = (
      this._workflow.services[this._workflow.services.length - 1] as Service
    ).availableFileOutputs;
    const wfAvailableFileInputs =
      this._workflow.input.availableFileInputs === 0
        ? firstService.availableFileInputs
        : this._workflow.input.availableFileInputs;

    this.setupInput(wfAvailableFileInputs);
    this.setupOutputOnLoad(this.availableFileOutputs);
    this.setServiceSettings();
    this.setupPrintSettings();
    this.setSteps();
  }
  get workflow(): Workflow {
    return this._workflow;
  }
  public get OutputType() {
    return OutputType;
  }

  @Input() set cloudRepos(value: []) {
    this._cloudRepos = value;
    if (value.length) {
      this.inputSources[1].options = value;
    } else {
      // remove the cloud repo option if there aren't any
      this.inputSources.splice(1, 1);
    }
  }
  get cloudRepos(): any {
    return this._cloudRepos;
  }

  @Output() update = new EventEmitter<any>();

  input: any = {
    uploadFiles: [],
    supportMutibleInputSource: false, // Will be used when supporting files from different input source
    cloudRepos: [], // type: CloudRepo[]
    cloudRepoInputs: [],
    currentCloudRepo: null,
    mergeDocs: [],
    maxSelection: 1,
    minSelection: 1,
    insertPageNumber: 1,
    maxInsertPageNumber: 2147483647,
    insertPageNumberError: '',
    isBatchInput: false,
    sendInput: false,
    sendInterim: true,
    sendCloudInput: false,
    sendCloudInterim: false,
    pauseInterim: false,
    editUsed: false,
    fileTypes: {
      raw: null,
      asAccept: null,
      asString: null,
    },
    selectedPages: '',
    selectedPagesError: '',
    selectedPagesValidation: /^[ ,\-\d]+$/,
    selectedPagesValidationError: 'SDE_USE_ONLY_NUMBERS',
  };

  output: any = {
    fileName: '',
    encryptionPassword: null,
    passwordProtection: false,
    sendEncryptedEmail: false,
    sendEncryptedEmailLabel: '',
    addDateTime: true,
    datetimestr: null,
    fileNameError: '',
    fileNameValidation: /^[^"?;<>|:'*%^&#+`\\\/]+$/,
    fileTypes: {
      raw: null,
      asArray: [],
      extension: { key: '', value: '' },
    },
    outputType: 0, // a mask condition of displaying any output option.
    selector: {
      items: [
        {
          label: 'SDE_DOWNLOAD',
          value: 'Download',
          glyph: 'xgl-file_download',
          isDefault: true,
          isOn: true,
        },
        {
          label: 'SDE_EMAIL2',
          value: 'Email',
          glyph: 'xgl-envelope',
          isDefault: false,
          isOn: false,
        },
        {
          label: 'SDE_CLOUD_REPO',
          value: 'Cloud',
          glyph: 'xgl-cloud_connect',
          isDefault: false,
          isOn: false,
        },
        {
          label: 'SDE_PRINT_AT_DEVICE',
          value: 'Print',
          glyph: 'xgl-print',
          isDefault: false,
          isOn: false,
        },
      ],
    },
    email: '',
    confirmEmail: '',
    emailSendOnErrorValue: false,
    emailOptions: {
      attachment: { visible: 'false', value: 'false' },
      sendCondition: { visible: 'true', value: 'always' },
      sourceLink: { visible: 'true', value: 'true' },
      sendInterim: { visible: 'true', value: 'true' },
      encryptAndTrack: { visible: 'true', value: 'true' },
      label: { visible: 'true', value: 'SDE_EMAIL2' },
      glyph: { visible: 'true', value: 'xgl-envelope' },
    },
  };
  outputSelected = true;
  passwordProtectionDisabled = false;
  passwordProtectionToolTip = '';
  passwordProtectionRegex = '^[ -~]+$';
  passwordProtectionError = 'SDE_INVALID_CHARACTERS';

  selectedInputSource: any;
  selectedCloudRepo: any;
  selectedCloudFolder: MWCloudItem | null | undefined;
  cloudRepoOptions: CloudRepo | null;
  inputSources = [
    {
      key: null,
      value: null,
      title: null,
      options: [
        {
          key: 'SDE_LOCAL_DRIVE',
          value: 'SDE_LOCAL_DRIVE',
          title: null,
          options: null,
        },
      ],
    },
    {
      key: 'SDE_CLOUD_REPOSITORIES',
      value: 'SDE_CLOUD_REPOSITORIES',
      title: 'SDE_CLOUD_REPOSITORIES',
      options: this._cloudRepos,
    },
  ];

  pageRangeOptions: any = [
    {
      label: 'SDE_ALL',
      value: 'SDE_ALL',
    },
    {
      label: 'SDE_SELECT_PAGES',
      value: 'SDE_SELECT_PAGES',
    },
  ];
  pageRangeOption: any = this.pageRangeOptions[0];

  private xdaTranslatePipe: XdaTranslate;
  constructor(
    public activeModal: NgbActiveModal,
    private workflowService: WorkflowService,
    private modalService: NgbModal,
    private workflowHelper: WorkflowHelperService,
    private xdaStringsService: XdaStringsService,
    private userService: UserService,
    private fileService: FileService,
    public middlewareService: MiddlewareService,
    public browserService: BrowserService,
    private accessListService: AccessListService,
  ) {
    this.xdaTranslatePipe = new XdaTranslate(this.xdaStringsService);
    this.isAdvancedPlan =
      (userService.user?.acquisition?.productPlanTier &&
        userService.user.acquisition.productPlanTier >=
          ProductPlanTier.Advanced) ||
      false;
    this.cloudRepoSub =
      this.middlewareService.directoryOrFilesSelected.subscribe((repoOptions) =>
        this.updateCloudRepoOptions(repoOptions),
      );
    this.allowedEmailDomains =
      this.userService.isAdvancedPlan &&
      this.accessListService.config?.enableDomainRestrictions
        ? this.xdaTranslatePipe.transform('SDE_FOLLOWING_EMAIL_DOMAINS', [
            this.accessListService.config.allowedDomains.join(', '),
          ])
        : '';

    this.licenseErrorSub = this.middlewareService.licenseErrorClosed.subscribe(() => {
      const outputStep = this.steps.find((x) => x.name === 'output');
      if (outputStep) {
        this.step = outputStep;
      }
    });
  }

  ngAfterViewInit() {
    this.serviceParams = this.serviceParamsQL.toArray();
    this.serviceParamsQL.changes.subscribe(() => {
      this.serviceParams = this.serviceParamsQL.toArray();
    });

    this.fileInputs = this.fileInputsQL.toArray();
    this.fileInputsQL.changes.subscribe(() => {
      this.fileInputs = this.fileInputsQL.toArray();
    });

    //display repo login
    if (this._workflow.input.isCloudInput && this.isAdvancedPlan) {
      this.selectInputRepo = true;
      this.selectCloudRepoInput(this.selectedInputSource);
    }

    this.passwordProtectionToolTip = this.xdaTranslatePipe.transform(
      'SDE_DOCUMENT_PASSWORD_NOT',
    );

  }

  showThisOutput(otype: OutputType) {
    return (otype & this.output.outputType) > 0;
  }

  toggleMergeMode() {
    this.isGoodToGo = true;

    // reset any XrxFileInputComponent components on the page
    this.fileInputs.forEach((XrxFileInputComponent) => {
      XrxFileInputComponent.resetInputSettings();
    });

    if (this.mergeMode.value === 'SDE_INSERT') {
      this.input.maxSelection = 2;
      this.input.minSelection = 2;
      this._workflow.input.mergeMode = MergeMode.Insert;
    } else if (this.mergeMode.value === 'SDE_CONCATENATE') {
      this.input.maxSelection = 5;
      this.input.minSelection = 2;
      this._workflow.input.mergeMode = MergeMode.Concatenate;
    } else {
      return;
    }

    this.input.uploadFiles = [];
    this.input.cloudRepoInputs = [];
    if (this.input.currentCloudRepo) {
      this.input.currentCloudRepo.files = null;
    }
  }

  setSteps() {
    let stepIndex = 0;
    this.steps = [];
    if (this.fileService.hasUploadedFileId) {
      this.input.uploadFiles = [{}];
      this.input.uploadFiles[0].name =
        this.fileService.currentFileName + '.pdf';
      this._workflow.output.file.name = this.fileService.currentFileName;
      this.output.fileName = this._workflow.output.file.name;
      this.allowSave = !this.userService.isGuestUser;
      this.disablePrintOption = this.userService.isGuestUser;

      if (this.isTranslateWorkflow) {
        this.output.fileTypes = this.getTranslationOutputFormat(
          this.input.uploadFiles[0].name,
        );
      }
    } else {
      this.steps.push({
        index: stepIndex++,
        name: 'input',
        buttonLabel: 'SDE_NEXT',
      });
    }

    // some services don't have service setting
    // check to see the first combined service setting's lenth
    if (this.serviceSettings[0].settings.length > 0) {
      this.steps.push({
        index: stepIndex++,
        name: 'service',
        buttonLabel: 'SDE_NEXT',
      });
    }

    this.steps.push({
      index: stepIndex++,
      name: 'output',
      buttonLabel: 'SDE_SCAN',
    });
    this.step = this.steps[0];
  }

  async stepForward(e: any, step: Step) {
    this.isGoodToGo = true;
    // run validation on any file input component so it will display any inline errors
    this.fileInputs.forEach((XrxFileInputComponent) => {
      XrxFileInputComponent.validateInput();
    });
    this.validateAllInputs();
    this.serviceParams.forEach((setting) => {
      if (setting.control) {
        setting.control.validate();
      }
    });

    if (this.specialCharactersInCloudFileName) {
      this.isGoodToGo = false;
    }

    // above validation failed
    if (!this.isGoodToGo) {
      return;
    }

    if (step.index < this.steps.length - 1) {
      if (step.index === 0) {
        // Note: the case of inputSource === local is covered by above file input/s validation
        if (this.inputSource.value === InputSource.Cloud) {
          if (this.isMergeWorkflow) {
            if (
              !this.input.cloudRepoInputs ||
              this.input.cloudRepoInputs.length < 2
            ) {
              this.isGoodToGo = false;
              return;
            }
          }
          // non merge workflow
          else if (
            !this.input.cloudRepoInputs ||
            this.input.cloudRepoInputs.length < 1
          ) {
            this.isGoodToGo = false;
            return;
          }
        }
      }

      // validate page range
      if (!this.validateRange()) {
        this.isGoodToGo = false;
        return;
      }

      // validate page number
      if (!this.validatePageNumber()) {
        this.isGoodToGo = false;
        return;
      }

      if (
        this.step.name === 'service' &&
        this.servicePageIndex < this.serviceSettings.length - 1
      ) {
        if (!this._workflow.isSingleService) {
          // for Combo workflows transfer selective setttings (e.g. language) from the current service to the next
          this.workflowService.transferServiceSettings(this.serviceSettings[this.servicePageIndex], this.serviceSettings[this.servicePageIndex+1]);
        }
        this.servicePageIndex++;
        this.isGoodToGo = true;
        return;
      }

      //advance the step
      this.step = this.steps[step.index + 1];

      if (
        this.step.name === 'service' &&
        this.serviceSettings[this.servicePageIndex].settings &&
        this.serviceSettings[this.servicePageIndex].validations
      ) {
        this.isGoodToGo = !Object.values(
          this.serviceSettings[this.servicePageIndex].validations,
        ).includes(false);
      } else if (this.step.name === 'output') {
        // If we have real cloud repo options, validate the token and path
        if (
          this.cloudRepoOptions &&
          this.cloudRepoOptions.connector &&
          this.output.selector.items.find(
            (x: Item) => x.label === 'SDE_CLOUD_REPO',
          ).isOn
        ) {
          await this.validateSelectedCloudRepo();
        }

        // If user is not a guest user, check to see if print option should be disabled or not
        if (!this.userService.isGuestUser) {
          this.disablePrintOption = false;
          const outputLanguages = this.serviceParams.find(
            (x) => x.setting.name2 === 'toLanguage',
          );
          if (outputLanguages) {
            this.disablePrintOption =
              outputLanguages.setting.value2.includes(',');
            if (this.disablePrintOption) {
              this.output.selector.items.find(
                (x: Item) => x.value === 'Print',
              ).isOn = false;
              this.outputSelected = this.isOutputSelected();
            }
          } else {
            this.setOutputRequiredIfProtectEmailDocument();
          }
        }

        // For Convert To PDF if archival setting is on then disable password protection.
        this.updatePasswordProtectionEnablement();
      } else {
        this.isGoodToGo = true;
      }
    }
  }

  stepBack(e: Event, step: Step) {
    this.isGoodToGo = true;
    if (step.index > 0) {
      if (this.step.name === 'service' && this.servicePageIndex > 0) {
        this.servicePageIndex--;
        return;
      }
      this.step = this.steps[step.index - 1];
    }
  }

  validateServiceSettings(e: any, field: any) {
    if (!this.serviceSettings[this.servicePageIndex].validations) {
      this.serviceSettings[this.servicePageIndex].validations = {};
    }

    this.serviceSettings[this.servicePageIndex].validations[field.name] = e;
    const values = Object.keys(
      this.serviceSettings[this.servicePageIndex].validations,
    ).map(
      (key) => this.serviceSettings[this.servicePageIndex].validations[key],
    );
    this.isGoodToGo = !values.includes(false);
  }

  back() {
    // Go to previous service
    this.onLastService = false;
    this.currentServiceIndex--;
    if (this.currentServiceIndex === 0) {
      this.onFirstService = true;
    }

    this.currentService = this._workflow.services[
      this.currentServiceIndex
    ] as Service;
  }

  canBePasswordProtected() {
    const ext = this.output.fileTypes.extension.value;
    if (
      ext === 'pdf' ||
      ext === 'docx' ||
      ext === 'doc' ||
      ext === 'pptx' ||
      ext === 'ppt' ||
      ext === 'xlsx'
    ) {
      return true;
    }
    return false;
  }

  canBeEdited() {
    return false;
  }

  isBatchInput() {
    return this.input.isBatchInput && !this.isMergeWorkflow;
  }

  containsTranslate() {
    const results = this._workflow.services.some((s) => {
      return (s as Service).name.toLowerCase().includes('translat');
    });
    return results;
  }

  async next(f: NgForm) {
    if (
      f.invalid ||
      (!this.outputSelected && this.workflow.output.outputRequired)
    ) {
      Object.values(f.controls).forEach((control) => {
        control.markAsDirty();
        control.markAsTouched();
      });
      return;
    } else if (
      this.output.selector.items.find((x: Item) => x.value === 'Cloud').isOn &&
      this.isAdvancedPlan
    ) {
      // have a selected cloud repo with no selected folder, so connect to the selected repo to login.
      if (this.selectedCloudRepo && !this.selectedCloudFolder) {
        this.selectCloudRepo(this.selectedCloudRepo, false);
        return;
      }

      if (!this.selectedCloudRepo || !this.selectedCloudFolder) {
        this.cloudRepoIsInvalid = true;
        return;
      } else {
        const cloudRepoIsValid = await this.validateSelectedCloudRepo();
        if (!cloudRepoIsValid) {
          return;
        }
      }
    }

    if (
      this.output.selector.items.find((x: Item) => x.value === 'Email').isOn
    ) {
      if (!this.accessListService.isAllowedDomain(this.output.email)) {
        this.errorEmail = '';
        setTimeout(() => {
          this.errorEmail = 'SDE_EMAIL_DOMAIN_NOT2';
        }, 100);
        this.form.form.get('confirmEmail')?.setErrors({ customerror: true });
        return;
      }
    }

    // MergerMode: Insert - set insert page number
    if (this._workflow.input.mergeMode === MergeMode.Insert) {
      // local source
      if (this.inputSource.value === InputSource.Local) {
        this.input.uploadFiles[1].insertPageNumber = Number(
          this.input.insertPageNumber,
        );
      }
      // cloud source
      else {
        this.input.cloudRepoInputs[1].insertPageNumber = Number(
          this.input.insertPageNumber,
        );
      }
    }
    this.setUserSelectedServiceSettings();

    // Set output filename and extension
    this._workflow.output.file.extension = FileType[
      this.output.fileTypes.extension.value
    ] as any;
    this._workflow.output.file.name = this.output.fileName;

    // use input file names to set output file names for batch process
    // batch from cloud repo
    if (this.input.cloudRepoInputs && this.input.cloudRepoInputs.length > 0) {
      this.input.cloudRepoInputs.forEach((bdoc: any) => {
        bdoc.outputFileName = bdoc.file.name.substr(
          0,
          bdoc.file.name.lastIndexOf('.'),
        );
      });
      this._workflow.input.cloudRepoOptions = this.input.currentCloudRepo;
      this._workflow.input.isCloudInput =
        this.inputSource.value === InputSource.Cloud;
    }
    // batch from local drive
    else {
      this.input.uploadFiles.forEach((bdoc: any) => {
        bdoc.outputFileName = bdoc.name.substr(0, bdoc.name.lastIndexOf('.'));
      });
    }

    // reset the output settings based on selected options
    this.setOutputSettings();

    this._workflow.output.sendEncryptedEmail = this.output.sendEncryptedEmail;
    if (this.passwordProtectionDisabled == true) {
      this._workflow.output.passwordProtection = false;
      this._workflow.output.encryptionPassword = null;
    } else {
      this._workflow.output.passwordProtection = this.output.passwordProtection;
      this._workflow.output.encryptionPassword = this.output.passwordProtection
        ? this.output.encryptionPassword
        : null;
    }

    // If output is Email set outputTo
    if (
      this.output.selector.items.find((x: Item) => x.value === 'Email').isOn
    ) {
      this._workflow.output.emailAddresses = this.output.email;
      this._workflow.output.fromEmailAddress = this.output.sendEncryptedEmail
        ? this.userService?.user?.email
        : '';
      this._workflow.input.sendInput = this.input.sendInput;
      this._workflow.input.sendInterim = this.input.sendInterim;
    } else {
      this._workflow.output.emailAddresses = '';
      this._workflow.output.fromEmailAddress = '';
    }

    if (
      this.output.selector.items.find((x: Item) => x.value === 'Cloud').isOn
    ) {
      this._workflow.input.sendCloudInput = this.input.sendCloudInput;
      this._workflow.input.sendCloudInterim = this.input.sendCloudInterim;
    }

    // When output is set to Print at Device, update the print options
    if (
      this.output.selector.items.find(
        (x: { value: string }) => x.value === 'Print',
      ).isOn
    ) {
      this.printSettings.forEach((setting) => {
        const printOption = this._workflow.output.printOptions?.find(
          (option) => option.key === String(setting['name']),
        );
        if (printOption) {
          // Convert 'quantity' value to string, otherwise assign as is
          printOption.value =
            String(setting['name']) === 'quantity'
              ? String(setting['value'])
              : String(setting['value']);
        }
      });
    }

    this._workflow.input.pauseInterim = this.input.pauseInterim;
    this._workflow.input.selectedPages =
      this.pageRangeOption.value == 'SDE_SELECT_PAGES'
        ? this.input.selectedPages
        : '';

    // reset advance plan only values to default if the plan isn't advanced
    if (!this.isAdvancedPlan) {
      this._workflow.input.selectedPages = '';
      this._workflow.output.sendEncryptedEmail = false;
    }

    // Return completed workflow and input file
    const result: WorkflowModalResult = {
      workflow: this._workflow,
      inputFile: this.input.uploadFiles,
      cloudRepoFiles: this.input.cloudRepoInputs,
      selectedPages: this._workflow.input.selectedPages,
      translateOutputFormat: !this.isTranslateWorkflow
        ? undefined
        : this.selectedTranslateFileType.value === 'Pdf'
          ? 'pdf'
          : 'auto',
    };
    this.activeModal.close(result);
  }

  resetInputSettings() {
    this.input.uploadFiles = [];
    this.pageRangeSupported = true;
    this.input.isBatchInput = false;
    this.specialCharactersInCloudFileName = false;

    // reset cloud input settings
    this.input.cloudRepoInputs = [];
    if (this._workflow.input.isCloudInput) {
      this.inputSource = this.inputSourceOptions[1];
      const wfInputRepo = this._workflow.input.cloudRepoOptions;
      this.selectedInputSource = this.cloudRepos.find(
        (x: any) =>
          x.value === wfInputRepo?.connector &&
          x.region === wfInputRepo?.region,
      );
      const inputCloudRepo = this.input.cloudRepos.find(
        (x: any) =>
          x.connector === wfInputRepo?.connector &&
          x.region === wfInputRepo?.region,
      );
      if (inputCloudRepo) {
        inputCloudRepo.files = [];
      }
    } else {
      this.selectedInputSource = this.inputSources[0].options[0];
      this.inputSource = this.inputSourceOptions[0];
    }

    this.input.selectedPages = this._workflow.input.selectedPages ?? '';
    const rangeInput = this.form.form.get('pageRange');
    if (rangeInput) {
      rangeInput.setValue(this._workflow.input.selectedPages ?? '');
    }
    this.pageRangeOption = this._workflow.input.selectedPages
      ? this.pageRangeOptions[1]
      : this.pageRangeOptions[0];

    // is document merge workflow
    if (this._workflow.input.maxConcatenatedDocument) {
      this.input.cloudRepoInputs = [];
      this.mergeMode =
        this.docMergeOptions[this.originalInputSettings.mergeMode];
      this.input.insertPageNumber =
        this.originalInputSettings.insertPageNumber || 1;
      this.input.minSelection = 2;
      if (this.mergeMode.value === 'SDE_INSERT') {
        this.input.maxSelection = 2;
        this._workflow.input.mergeMode = MergeMode.Insert;
      } else if (this.mergeMode.value === 'SDE_CONCATENATE') {
        this.input.maxSelection = 5;
        this._workflow.input.mergeMode = MergeMode.Concatenate;
      }

      if (this.originalInputSettings.mergeDocs) {
        this.input.mergeDocs = this.originalInputSettings.mergeDocs.map(
          (mdoc: any) => ({
            insertPageNumber: mdoc.insertPageNumber,
          }),
        );
      }
    }

    this.isGoodToGo = true;
    // reset any XrxFileInputComponent components on the page
    this.fileInputs.forEach((XrxFileInputComponent) => {
      XrxFileInputComponent.resetInputSettings();
    });
  }

  resetServiceSettings() {
    // restore the original service settings for the current service page only! The binding with xrx-setting will reset the UI.
    this.serviceSettings[this.servicePageIndex] = JSON.parse(
      JSON.stringify(this.originalServiceSettings[this.servicePageIndex]),
    );

    if (!this._workflow.isSingleService && this.servicePageIndex > 0) {
      // for Combo workflows transfer selective setttings (e.g. language) from the previous service to the current service
      this.workflowService.transferServiceSettings(this.serviceSettings[this.servicePageIndex - 1], this.serviceSettings[this.servicePageIndex]);
    }

    this.isGoodToGo = true;
  }

  setOutputFormatIfIsTranslateService(fileName: string | null = null) {
    if (this.inputSource.value === 'SDE_CLOUD_REPO') {
      fileName ??= this.input.cloudRepoInputs[0].file.name;
    } else {
      fileName ??= this.input.uploadFiles[0].name;
    }

    if (this.isTranslateWorkflow) {
      this.output.fileTypes = this.getTranslationOutputFormat(fileName);
    }
  }

  setOutputRequiredIfProtectEmailDocument() {
    // For Protect workflow, check emailDocument setting to indicate if output is required
    if (
      this.serviceSettings[this.servicePageIndex].name ===
      'SDE_DOCUMENT_PROTECTION'
    ) {
      const settings = JSON.parse(
        this.serviceSettings[this.servicePageIndex].settings[0]
          .value as string,
      );
      this.workflow.output.outputRequired = !settings.emailDocument; // output is required if RPost is not emailing the document
    }
  }

  resetOutputSettings() {
    this.selectInputRepo = false;
    this.passwordProtectionDisabled = false;
    this._workflow.output = JSON.parse(
      JSON.stringify(this.originalWorkflow.output),
    );
    this.setupOutputOnLoad(this.availableFileOutputs);
    this.setOutputSettings();
    if (!this.disablePrintOption) {
      this.setupPrintSettings();
    }
    this.setOutputFormatIfIsTranslateService();
    this.setOutputRequiredIfProtectEmailDocument();
 
    this.output.encryptionPassword = null;
    let fileName = '';
    if (this.inputSource.value === 'SDE_CLOUD_REPO') {
      fileName = this.input.cloudRepoInputs[0].file.name;
    } else {
      fileName = this.input.uploadFiles[0].name;
    }
    this.output.fileName = fileName.substr(0, fileName.lastIndexOf('.'));
    this.input.sendInput = this.workflow.input.sendInput;
    this.input.sendInterim = this.workflow.input.sendInterim;
    this.input.sendCloudInterim = this.workflow.input.sendCloudInterim;
    this.input.pauseInterim = this.workflow.input.pauseInterim;
    this.isGoodToGo = true;
  }

  // Convert fileType enum into useable string and get extension for output file
  setupOutputOnLoad(fType: FileType) {
    // Regex for do not allow the following characters: \/:*?"<>|';%^&#+`
    this.output.fileNameError = this.xdaTranslatePipe.transform(
      'SDE_CHARACTERS_CANNOT_BE',
      ['"?;<>|:\'*%^&#+`\\\/'],
    );
    this.output.fileTypes.raw = fType;
    this.output.fileTypes.asArray = this.workflowHelper
      .fileTypesToStrings(this.output.fileTypes.raw)
      .map((e) => {
        return { key: `.${e.toLowerCase()}`, value: e };
      });
    const value = this.workflow.output.file?.extension
      ? this.workflowHelper.fileTypesToStrings(
          this.workflow.output.file.extension,
        )[0]
      : this.workflowHelper.fileTypesToStrings(this.output.fileTypes.raw)[0];
    this.output.fileTypes.extension =
      this.output.fileTypes.asArray.find((e: any) => e.value === value) ||
      this.output.fileTypes.asArray[0];
    this.output.addDateTime =
      this.workflow.output.file?.appendDateTimeToFilename;
    this.selectedTranslateFileType =
      this.isTranslateWorkflow &&
      this._workflow.output.file.extension === FileType.pdf
        ? this.translateFileTypes[1]
        : this.translateFileTypes[0];

    // If using a private/shared workflow use the saved values, or if changes have been made to the output options (email or print)
    // for Xerox workflows use the selector defaults
    if (this.workflow.type == 1 || this.workflow.output.outputOptions) {
      this.output.selector.items.forEach((item: Item) => {
        item.isOn = false;
      });
      this.workflow.output.outputOptions?.forEach((option: any) => {
        const foundOutputOption = this.output.selector.items.find(
          (x: Item) => x.value === option.value,
        );
        if (foundOutputOption) {
          if (!this.isAdvancedPlan && option.value == 'Cloud') {
            foundOutputOption.isOn = false;
          } else {
            foundOutputOption.isOn = true;
          }
        }
      });
      if (this.disablePrintOption) {
        this.output.selector.items.find((x: Item) => x.value === 'Print').isOn =
          false;
      }
    } else {
      this.output.selector.items.forEach((item: Item) => {
        item.isOn = item.isDefault;
      });
    }
    this.updatePasswordProtectionEnablement();
    this.output.confirmEmail = this.output.email = this.workflow.output
      .emailAddresses
      ? this.workflow.output.emailAddresses
      : this.userService.user?.email;

    // Iphones dont handle the href well???
    if (this.browserService.isMobile) {
      this.output.sendEncryptedEmailLabel =
        this.xdaTranslatePipe.transform('SDE_ENCRYPT_TRACK1') +
        ' ' +
        this.xdaTranslatePipe.transform('SDE_FMTSTR_BY_FMTSTR', [
          '(RMail<sup>&reg;</sup>',
          'RPost<sup>&reg;</sup>) ',
        ]) +
        this.xdaTranslatePipe.transform('SDE_FMTSTR_CREDITS', ['5']);
    } else {
      this.output.sendEncryptedEmailLabel =
        this.xdaTranslatePipe.transform('SDE_ENCRYPT_TRACK_EMAIL1') +
        ' <a href="https://rmail.com" target="_blank">' +
        this.xdaTranslatePipe.transform('SDE_FMTSTR_BY_FMTSTR', [
          'RMail<sup>&reg;</sup>',
          'RPost<sup>&reg;</sup>',
        ]) +
        '</a>. ' +
        this.xdaTranslatePipe.transform('SDE_FMTSTR_CREDITS', ['5']);
    }

    this.output.passwordProtection = this.workflow.output.passwordProtection;
    this.output.sendEncryptedEmail = this.workflow.output.sendEncryptedEmail;
    this.output.emailSendOnErrorValue = false;
    this.output.outputType = this.workflow.output.outputType;

    this.output.emailOptions.sourceLink =
      this.getOutputEmailOption('sourceLink');
    this.output.emailOptions.attachment =
      this.getOutputEmailOption('attachment');
    this.output.emailOptions.sendCondition =
      this.getOutputEmailOption('sendCondition');
    this.output.emailOptions.sendInterim =
      this.getOutputEmailOption('sendInterim');
    this.output.emailOptions.encryptAndTrack =
      this.getOutputEmailOption('encryptAndTrack');
    this.output.emailOptions.label = this.getOutputEmailOption('label');
    this.output.emailOptions.glyph = this.getOutputEmailOption('glyph');

    // set output.emailSendOnErrorValue based on the workflow's email setting value for condition
    if (
      this.workflow.output.emailOptions &&
      this.workflow.output.emailOptions[0].value == EmailSendCondition.OnFailure
    ) {
      this.output.emailSendOnErrorValue = true;
    }

    this.setupCloudRepoOptions(true);

    this.outputSelected = this.isOutputSelected();
  }

  getOutputEmailOption(optionKey: string) {
    const emOption = this.workflow.output.emailOptions?.find(
      (i) => i.key === optionKey,
    );
    return { value: emOption?.value, visible: emOption?.visible };
  }

  setOutputSettings() {
    let emailCondition = '',
      workflowOutputs = this.output.selector.items.filter((x: Item) => x.isOn);

    // clear and recreate the output settings based on selected options
    this.workflow.output.outputOptions = [];
    this.workflow.output.file.appendDateTimeToFilename =
      this.output.addDateTime;

    if (workflowOutputs.length > 0) {
      workflowOutputs.forEach((item: Item) => {
        this.workflow.output.outputOptions?.push({
          key: 'outputType',
          value: item.value,
        });

        if (item.value === 'Email') {
          this.workflow.output.emailAddresses = this.output.email;
          this.workflow.output.fromEmailAddress = this.output.sendEncryptedEmail
            ? this.userService?.user?.email
            : '';

          // set email send condition
          if (
            this.output.selector.items.find((x: Item) => x.value === 'Email')
              .isOn
          ) {
            emailCondition = this.output.emailSendOnErrorValue
              ? EmailSendCondition.OnFailure
              : EmailSendCondition.Always;
          }
        }
      });
    } else {
      emailCondition = EmailSendCondition.Never;

      const emailOption = this.workflow.output.emailOptions?.find(
        (i) => i.key === 'sendCondition',
      );
      if (emailOption) {
        emailOption.value = emailCondition;
      }
    }

    this.workflow.output.cloudRepoOptions = this.cloudRepoOptions;
    this.workflow.output.passwordProtection = this.output.passwordProtection;
    this.workflow.output.sendEncryptedEmail = this.output.sendEncryptedEmail;
  }

  setupPrintSettings() {
    this.printSettings = JSON.parse(JSON.stringify(Settings.Print));
    if (this.workflow.output.printOptions) {
      this.printSettings.forEach((setting: any) => {
        const printOption = this.workflow.output.printOptions?.find(
          (x: any) => x.key == setting.name,
        );
        if (printOption) {
          const selectedValue = setting.options.find(
            (x: any) => x.value === printOption.value,
          );
          if (selectedValue) {
            setting.selectedValue = selectedValue;
            setting.value = printOption.value;
          } else {
            setting.selectedValue.value = printOption.value;
            setting.value = printOption.value;
          }
        }
      });
    }

    // TODO Remove stapling if device doesn't support
  }

  setupCloudRepoOptions(firstSetup = false) {
    if (this.isAdvancedPlan && this.workflow.output.cloudRepoOptions) {
      const repoOptions = this.workflow.output.cloudRepoOptions;
      this.selectedCloudRepo = this.cloudRepos.find((x: any) =>
        !repoOptions.region
          ? x.value === repoOptions.connector
          : x.value === repoOptions.connector &&
            x.region === repoOptions.region,
      );
      repoOptions.region = this.selectedCloudRepo?.region
        ? this.selectedCloudRepo.region
        : repoOptions.region; // update region from selected repo setting
      this.selectedCloudFolder = repoOptions.dir;

      this.middlewareService.region = repoOptions.region ?? '';
      this.middlewareService.connector = repoOptions.connector;
      if (firstSetup) {
        this.middlewareService.connectorToken = repoOptions.access ?? '';
        this.middlewareService.connectorRefreshToken =
          repoOptions.refresh ?? '';
        this.middlewareService.repoOptions = repoOptions;
      } else {
        this.middlewareService.connectorToken =
          (this.middlewareService.connectorToken || repoOptions.access) ?? '';
        this.middlewareService.connectorRefreshToken =
          (this.middlewareService.connectorRefreshToken ||
            repoOptions.refresh) ??
          '';
      }

      this.updateCloudRepoOptions(this.workflow.output.cloudRepoOptions);
      this.input.sendCloudInput = this.workflow.input.sendCloudInput;
    }
  }

  // Convert fileType enum into usable string and accept string for file input
  setupInput(fType: FileType) {
    this.input.fileTypes.raw = fType;
    this.input.fileTypes.asAccept = `.${this.workflowHelper.fileTypesToStrings(this.input.fileTypes.raw, true).join(',.')},${this.workflowHelper.fileTypesToMIMETypes(this.input.fileTypes.raw).join(',')}`;
    this.input.fileTypes.asString = this.workflowHelper
      .fileTypesToStrings(this.input.fileTypes.raw)
      .join(', ');
    this.input.sendInput = this.workflow.input.sendInput;
    this.input.sendInterim = this.workflow.input.sendInterim;
    this.input.pauseInterim = this.workflow.input.pauseInterim;
    this.input.sendCloudInterim = this.workflow.input.sendCloudInterim;
    this.input.maxSelection = this.workflow.input.maxConcatenatedDocument
      ? this.workflow.input.maxConcatenatedDocument
      : 5;

    if (this.workflow.input.selectedPages) {
      this.input.selectedPages = this.workflow.input.selectedPages;
    }

    if (this.workflow.input.insertPageNumber) {
      this.input.insertPageNumber = this.workflow.input.insertPageNumber;
    }

    this.pageRangeOption = this.workflow.input.selectedPages
      ? this.pageRangeOptions[1]
      : this.pageRangeOptions[0];
    this.isMergeWorkflow =
      this.workflow.input.maxConcatenatedDocument +
        this.workflow.input.maxInsertedDocument >
      0
        ? true
        : false;

    // remove batch processing for react, summarize or fax related workflows
    if (
      !this.isMergeWorkflow &&
      this.workflow.services.find(
        (x: any) =>
          x.name === 'SDE_REDACT' ||
          x.name === 'SDE_EXTRACTIVE_SUMMARY' ||
          x.name === 'SDE_SEND_FAX',
      )
    ) {
      this.input.maxSelection = 1;
    }

    this.inputSourceOptions = [
      {
        value: 'SDE_LOCAL_FILE',
        desc: null,
      },
      {
        value: 'SDE_CLOUD_REPO',
        desc: null,
      },
    ];
    this.inputSource = this.inputSourceOptions[0];
    if (this._workflow.input.isCloudInput && this.isAdvancedPlan) {
      this.inputSource = this.inputSourceOptions[1];
      const wfInputRepo = this._workflow.input.cloudRepoOptions;
      this.selectedInputSource = this.cloudRepos.find((x: any) =>
        !wfInputRepo?.region
          ? x.value === wfInputRepo?.connector
          : x.value === wfInputRepo?.connector &&
            x.region === wfInputRepo?.region,
      );
    } else {
      this.selectedInputSource = this.inputSources[0].options[0];
    }

    // Document Merge workflow
    if (this.isMergeWorkflow) {
      this.docMergeOptions = [
        {
          value: 'SDE_CONCATENATE',
          desc: this.xdaTranslatePipe.transform('SDE_MERGE_UP_FMTSTR', ['5']),
        },
        {
          value: 'SDE_INSERT',
          desc: 'SDE_INSERT_SINGLE_DOCUMENT',
        },
      ];
      this.mergeMode = this.docMergeOptions[this._workflow.input.mergeMode];
      this.input.minSelection = 2;
      this.input.maxSelection =
        this._workflow.input.mergeMode == MergeMode.Insert ? 2 : 5;
    } else {
      this._workflow.input.mergeMode = MergeMode.None;
    }

    // store the original input setting for reset input.
    this.originalInputSettings = JSON.parse(
      JSON.stringify(this._workflow.input),
    );
  }

  setServiceSettings() {
    // initialize an empty CombinedServiceSettings with proper type for validations
    const initialCombined: CombinedServiceSettings = {
      name: '',
      settings: [],
      validations: {} as Record<string, boolean>,
    };

    this.serviceSettings = [];
    let combinedSettings: CombinedServiceSettings = initialCombined;
    this.serviceSettings.push(combinedSettings);

    this.workflow.services.forEach((s, index) => {
      const service = s as Service;
      if (service.serviceSettings && service.serviceSettings.length) {
        // This service setting has own page
        if (index > 0 && service.pageBreak) {
          combinedSettings = {
            name: '',
            settings: [],
            validations: {} as Record<string, boolean>,
          };
          combinedSettings.name = service.name;
          this.serviceSettings.push(combinedSettings);
        }

        combinedSettings.name = service.name;

        service.serviceSettings.forEach((param) => {
          combinedSettings.settings.push(param);
        });
      }
    });

    //  store the original service setting for reset service.
    this.originalServiceSettings = JSON.parse(
      JSON.stringify(this.serviceSettings),
    );
  }

  optionsToDictionary(param: any) {
    if (param.value) {
      param.selectedValue = param.options.find(
        (option: any) => option.value === param.value,
      );
    }
    if (param.value2) {
      param.selectedValue2 = param.options.find(
        (option: any) => option.value === param.value2,
      );
    }
    return param.options;
  }

  // force xrx-input to change the value display to include or not include "[Date & Time]"
  refreshOutputFilename() {
    if (this.output.fileName) {
      const fileName = this.output.fileName;
      this.output.fileName = this.output.fileName + 1;
      setTimeout(() => {
        this.output.fileName = fileName;
      }, 10);
    }
  }

  handleUploadFileInputs(prams: any) {
    this.input.uploadFiles = [];
    this.input.uploadFiles.push(...prams.files);
    this.fileInputIsValid = prams.isValid;

    if (prams.files.length < 1) {
      this.pageRangeSupported = true;
    }

    this.input.uploadFiles.forEach((f: any, i: any) => {
      f.fileFormat = this.checkFileNameAndFormat(f.name, i === 0);
      f.outputFileName = f.name.substr(0, f.name.lastIndexOf('.'));
    });

    this.input.isBatchInput = this.input.uploadFiles.length > 1;
    this.checkPageRange();
  }

  handleSelectedFileFromCloud(fileName: string) {
    if (!this.checkFileNameAndFormat(fileName)) {
      return false;
    }
    this.isGoodToGo =
      this.input.cloudRepoInputs && this.input.cloudRepoInputs.length > 0
        ? true
        : false;

    return this.isGoodToGo;
  }

  checkFileNameAndFormat(fileName: string, setOutputFileName = true) {
    if (setOutputFileName) {
      this.output.fileName = fileName.substr(0, fileName.lastIndexOf('.'));
    }

    const fileFormat = this.getFileFormat(fileName);
    if (!fileFormat) {
      this.fileFormatNotSupported = true;
      this.isGoodToGo = false;
      return null;
    }

    if (this.input.fileTypes.asAccept.indexOf(fileFormat) === -1) {
      this.fileFormatNotSupported = true;
      this.isGoodToGo = false;
      return null;
    }

    this.checkIsPageRangeSupported(fileFormat);
    this.setOutputFormatIfIsTranslateService(fileName);
    return fileFormat;
  }

  getOutputFormatForBatchInput(fileName: any) {
    if (!this.isTranslateWorkflow) {
      return this.output.fileTypes.extension.value;
    } else if (this.selectedTranslateFileType.value !== 'Auto') {
      return 'pdf';
    }

    // handle translation with Auto type
    return this.getTranslationOutputFormat(fileName)?.extension.value;
  }

  getTranslationOutputFormat(fileName: any) {
    if (this.isTranslateWorkflow) {
      let inputType = this.lastService.availableFileInputs;
      if (this._workflow.services.length > 1) {
        inputType = (
          this._workflow.services[this._workflow.services.length - 2] as Service
        ).availableFileOutputs;
      }
      return this.workflowHelper.getTranslateFileTypes(fileName, inputType);
    }
    return;
  }

  validateSpecialCharacters(file: any) {
    return /^[^"?;<>|:'*%^&#+`\\\/]+$/.test(file.name);
  }

  getFileFormat(fileName: string) {
    const index = fileName.lastIndexOf('.');
    if (index === -1) {
      return null;
    }
    return fileName.substr(index + 1).toLowerCase();
  }

  checkIsPageRangeSupported(fileFormat: string) {
    const exts = ['doc', 'docx', 'ppt', 'pptx', 'tif', 'tiff', 'pdf', 'rtf'];
    this.pageRangeSupported = exts.includes(fileFormat);
    this.checkPageRange();
  }

  checkPageRange() {
    if (!this.pageRangeSupported || this.input.isBatchInput) {
      this.input.selectedPages = '';
    }
  }

  toggleInputSource() {
    let fileName = '';
    this.isGoodToGo = true; // reset when input source is changed

    if (
      this.inputSource === this.inputSourceOptions[0] &&
      this.input.uploadFiles[0].name
    ) {
      fileName = this.input.uploadFiles[0].name;
    } else if (
      this.inputSource === this.inputSourceOptions[1] &&
      this.input.cloudRepoInputs[0].name
    ) {
      fileName = this.input.cloudRepoInputs[0].name;
    }

    const fileFormat = this.getFileFormat(fileName);
    if (fileFormat) {
      this.checkIsPageRangeSupported(fileFormat);
    }

    this.output.fileName = fileName;
  }

  fileDragDropBody(event: DragEvent) {
    event.preventDefault();
  }

  validateAllInputs() {
    this.fileInputs.forEach((fileInputComponent) => {
      if (
        (fileInputComponent.required &&
          !fileInputComponent.input.uploadFile.name) ||
        !fileInputComponent.isValid
      ) {
        this.isGoodToGo = false;
      }
    });
  }

  setMainUploadFile() {
    for (let i = 0; i < this.input.mergeDocs.length; i++) {
      const doc = this.input.mergeDocs[i];
      if (doc.file) {
        this.input.uploadFile = doc.file;
        this.fileInputIsValid = true;
        this.output.fileName = doc.file.name.substr(
          0,
          doc.file.name.lastIndexOf('.'),
        );
        break;
      }
    }
  }

  validateFileInputs() {
    this.fileInputs.forEach((fileInputComponent) => {
      fileInputComponent.ngControl.control.markAsTouched();
    });

    this.isGoodToGo =
      this.input.mergeDocs[0].file && this.input.mergeDocs[1].file
        ? true
        : false;
  }

  moveInputFileUp(docList: any[], i: number) {
    if (!docList[i].file || i === 0) {
      return;
    }
    // swap
    const upDoc = docList[i - 1];
    docList[i - 1] = docList[i];
    docList[i] = upDoc;
    if (this.inputSource === this.inputSourceOptions[0]) {
      this.setMainUploadFile();
      this.validateFileInputs();
    } else {
      this.output.fileName = docList[0].file.name.substr(
        0,
        docList[0].file.name.lastIndexOf('.'),
      );
    }
  }

  moveInputFileDown(docList: any[], i: number) {
    if (!docList[i].file || i === 0 || i === docList.length - 1) {
      return;
    }

    // swap
    const lowDoc = docList[i + 1];
    docList[i + 1] = docList[i];
    docList[i] = lowDoc;
    if (this.inputSource === this.inputSourceOptions[0]) {
      this.validateFileInputs();
    }
  }

  deleteWorkflowNoModal(workflow: Workflow) {
    this.workflowService.deleteWorkflow(workflow).subscribe(
      () => {
        setTimeout(() => {
          this.update.emit(null);
        });
      },
      (error) => {
        console.log(error);
      },
    );
  }

  saveWorkflow() {
    const modalRef = this.modalService.open(SaveAsNewModalComponent, {
      windowClass: 'xrx-modal',
    });
    modalRef.componentInstance.name = this.canEditWorkflow
      ? this.xdaStringsService.strings[this.workflow.name] || this.workflow.name
      : '';
    modalRef.componentInstance.private = !this.workflow.isShared;
    modalRef.componentInstance.icon = JSON.parse(
      JSON.stringify(this.workflow.icon),
    );
    modalRef.componentInstance.saveAsNew = !this.canEditWorkflow;

    modalRef.result.then(
      (saveAsData) => {
        // Make a deep copy of workflow
        const originalWorkflow: Workflow = JSON.parse(
          JSON.stringify(this.workflow),
        );
        // Set workflow name
        this.workflow.name = saveAsData.name;
        this.workflow.parentId = originalWorkflow.parentId
          ? originalWorkflow.parentId
          : (originalWorkflow.id ?? undefined);
        // Set workflow icon
        if (!saveAsData.isDefaultIcon) {
          this.workflow.icon = saveAsData.icon;
        }

        this.setWorkflowUserSelectedSettings();
        this.setOutputSettings();

        //If output is Download set output filename and extension
        if (
          this.output.selector.items.find((x: Item) => x.value === 'Download')
            .isOn
        ) {
          this.workflow.output.file.name = this.output.fileName;
          this.workflow.output.file.extension = FileType[
            this.output.fileTypes.extension.value
          ] as any;
        }
        this.workflow.isShared = !saveAsData.private;

        if (saveAsData.saveAsNew) {
          // If the save modal was set up to save a new workflow
          this.workflowService
            .createWorkflow(this.workflow, !this.workflow.isShared)
            .subscribe(
              (newWorkflowId) => {
                if (newWorkflowId) {
                  this.workflow.id = newWorkflowId;
                }
                this.update.emit(null);
              },
              () => {
                this.workflow = originalWorkflow;
                this.step = this.steps[this.steps.length - 1];
                const modalRef = this.modalService.open(BasicAlertComponent);
                modalRef.componentInstance.title =
                  'SDE_ERROR_CREATING_WORKFLOW';
              },
            );
        } else if (
          !saveAsData.saveAsNew &&
          originalWorkflow.isShared != this.workflow.isShared
        ) {
          // if updating the existing workflow, but changing the Sharing
          // create a new workflow and delete the previous since the sharing changes the primary key
          this.workflowService
            .createWorkflow(this.workflow, !this.workflow.isShared)
            .subscribe(
              (newWorkflowId) => {
                if (newWorkflowId) {
                  this.workflow.id = newWorkflowId;
                }
                this.update.emit(null);
                this.deleteWorkflowNoModal(originalWorkflow);
              },
              () => {
                this.workflow = originalWorkflow;
                this.step = this.steps[this.steps.length - 1];
                const modalRef = this.modalService.open(BasicAlertComponent);
                modalRef.componentInstance.title =
                  'SDE_ERROR_CREATING_WORKFLOW';
              },
            );
        } else {
          // If the save modal was set to update the current workflow
          this.workflowService.saveWorkflow(this.workflow).subscribe(
            (updated) => {
              if (updated) {
                this.update.emit(null);
              } else {
                // user doesn't have permission to update this workflow
                const modalRef = this.modalService.open(BasicAlertComponent);
                modalRef.componentInstance.title = 'SDE_ACCESS_DENIED_NORIGHTS';
              }
            },
            (error) => {
              this.workflow = originalWorkflow;
              this.step = this.steps[this.steps.length - 1];
              const modalRef = this.modalService.open(BasicAlertComponent);
              modalRef.componentInstance.title = 'SDE_ERROR_UPDATING_WORKFLOW';
              console.log(error);
            },
          );
        }
      },
      () => {},
    );
  }

  /**
   * call by saveWorkflow
   */
  setWorkflowUserSelectedSettings() {
    this.workflow.output.file.name = this.output.fileName;
    this.workflow.output.file.extension = FileType[
      this.output.fileTypes.extension.value
    ] as any;
    this.workflow.output.file.appendDateTimeToFilename =
      this.output.addDateTime;
    this.workflow.input.sendInput = this.input.sendInput;
    this.workflow.input.sendCloudInput = this.input.sendCloudInput;
    this.workflow.input.sendCloudInterim = this.input.sendCloudInterim;
    this.workflow.input.sendInterim = this.input.sendInterim;
    this.workflow.input.selectedPages =
      this.pageRangeOption.value == 'SDE_SELECT_PAGES'
        ? this.input.selectedPages
        : '';
    this.workflow.input.insertPageNumber = Number(this.input.insertPageNumber);
    this.workflow.input.isCloudInput =
      this.inputSource.value === InputSource.Cloud;
    this.workflow.input.pauseInterim = this.input.pauseInterim;
    this.workflow.input.isScannedInput = false;
    if (this.workflow.input.isCloudInput) {
      this.workflow.input.cloudRepoOptions = this.input.currentCloudRepo;
      if (this.workflow.input.cloudRepoOptions) {
        this.workflow.input.cloudRepoOptions.files = null;
      }
    }

    this.setUserSelectedServiceSettings();

    if (
      this.output.selector.items.find((x: Item) => x.value === 'Print').isOn
    ) {
      // set print settings
      this.printSettings.forEach((setting: any) => {
        const printOption = this.workflow.output.printOptions?.find(
          (option) => option.key == setting.name,
        );
        if (printOption != null) {
          const selectedValue =
            typeof setting.selectedValue == 'object'
              ? setting.selectedValue.value
              : setting.selectedValue;
          printOption.value = selectedValue.toString();
        }
      });
    }
  }

  setUserSelectedServiceSettings() {
    this.serviceSettings.forEach((s) => {
      const svsettings = this.workflow.services.find(
        (x: any) => x.name === s.name,
      ) as Service;
      if (svsettings) {
        svsettings.serviceSettings = s.settings;
      }
    });
  }

  updateOutputOptions(outputOption: Item) {
    outputOption.isOn = !outputOption.isOn;
    this.outputSelected = this.isOutputSelected();
    this.updatePasswordProtectionEnablement();
  }

  isOutputSelected() {
    return this.output.selector.items.some((item: Item) => item.isOn == true);
  }

  isGoogleTranslation() {
    return (
      this.isTranslateWorkflow &&
      this.xdaStringsService.strings['TRANSLATION_ENGINE'] === 'Google'
    );
  }

  updatePasswordProtectionEnablement() {
    // if print is the only selected output disable document password protection
    const destinationsSelected = this.output.selector.items.filter(
      (item: Item) => item.isOn == true,
    );
    if (
      destinationsSelected.length == 1 &&
      destinationsSelected[0].value == 'Print'
    ) {
      this.passwordProtectionDisabled = true;
    } else {
      this.passwordProtectionDisabled = false;
    }

    // For Convert to PDF if archival setting is on then disable password protection.
    if (
      this.serviceSettings[this.servicePageIndex]?.name ===
      'SDE_CONVERT_TO_PDF1'
    ) {
      const selectedValue = this.serviceSettings[this.servicePageIndex]
        .settings[1].value as string;
      const settings = JSON.parse(selectedValue);
      this.passwordProtectionDisabled = settings.compatibility.includes('pdfA')
        ? true
        : false;
      this.passwordProtectionToolTip = this.passwordProtectionDisabled
        ? this.xdaTranslatePipe.transform('SDE_DOCUMENT_PASSWORD_NOT1')
        : '';
      if (this.passwordProtectionDisabled) {
        this.output.passwordProtection = false;
      }
    }
  }

  /**
   * Select output repo
   * @param selectedCloudRepo
   * @param returning
   */
  selectCloudRepo(selectedCloudRepo: any, returning = false) {
    if (!selectedCloudRepo) {
      return;
    }

    this.selectedCloudFolder = null;
    this.selectInputRepo = false;
    this.middlewareService.initiateWebLogin(
      { connector: selectedCloudRepo.value, region: selectedCloudRepo.region },
      returning,
    );
    if (!returning) {
      this.updateCloudRepoOptions({
        connector: selectedCloudRepo.value,
        region: selectedCloudRepo.region,
      });
    }
  }

  changeCloudFolder() {
    // Don't allow browse until there's an access token
    if (!this.cloudRepoOptions?.access) {
      return;
    }

    const modalRef = this.modalService.open(MiddlewareBrowseModalComponent, {
      windowClass: 'xrx-modal',
      backdrop: 'static',
    });
    modalRef.componentInstance.returning = true;
    modalRef.componentInstance.returningRepo = {
      connector: this.cloudRepoOptions.connector,
      region: this.cloudRepoOptions.region,
      accessToken: this.cloudRepoOptions.access,
      refreshToken: this.cloudRepoOptions.refresh,
      sitePath: this.cloudRepoOptions.sitePath,
      dir: this.cloudRepoOptions.dir,
      selectedFolders: null,
    };

    modalRef.result
      .then((repoOption) => {
        this.updateCloudRepoOptions(repoOption);
      })
      .catch((ex) => {
        // display repo login only if access is not set
        if (ex && this.cloudRepoOptions?.access === '') {
          this.selectCloudRepo(
            { key: '', value: this.cloudRepoOptions.connector },
            true,
          );
        }
      });
  }

  updateCloudRepoOptions(repoOption: CloudRepo) {
    // check access instead of file as the user may not select any files.
    if (this.selectInputRepo) {
      return this.updateCloudRepoInputs(repoOption);
    }

    if (repoOption == null || !repoOption.connector) {
      this.cloudRepoOptions = null;
      this.selectedCloudFolder = null;
    } else {
      this.cloudRepoOptions = repoOption;
      const sitePath =
        repoOption.sitePath && repoOption.sitePath.length
          ? repoOption.sitePath[0]
          : null;
      const cloudRepoPath = repoOption.dir || sitePath;
      if (cloudRepoPath && repoOption.connector !== '') {
        // Set to value
        this.selectedCloudFolder = Object.assign({}, cloudRepoPath);
        if (cloudRepoPath.name) {
          // Remove Trailing /
          if (this.selectedCloudFolder.name.endsWith('/')) {
            this.selectedCloudFolder.name = this.selectedCloudFolder.name.slice(
              0,
              -1,
            );
          }
          // Get last segment
          this.selectedCloudFolder.name =
            this.selectedCloudFolder.name.substring(
              this.selectedCloudFolder.name.lastIndexOf('/') + 1,
            );
        } else {
          // Root folder selected, display repo name
          this.selectedCloudFolder.name = this.selectedCloudRepo.key;
        }
      }
    }
  }

  selectInputSource(inputSource: any) {
    this.input.uploadFiles = [];
    this.input.cloudRepoInputs = null;
    if (inputSource.value.includes('SDE_LOCAL')) {
      this.inputSource = this.inputSourceOptions[0];
    } else {
      this.selectInputRepo = true;
      this.inputSource = this.inputSourceOptions[1];
      this.selectCloudRepoInput(inputSource);
    }
  }

  /**
   * Select input cloud repo
   * @param cloudRepo
   * @param returning
   */
  selectCloudRepoInput(selectedCloudRepo: any, returning = false) {
    // reset cloudFileName
    this.input.cloudRepoInputs = [];
    // pass input fileTypes
    this.middlewareService.initiateWebLogin(
      { connector: selectedCloudRepo.value, region: selectedCloudRepo.region },
      returning,
      this.input.fileTypes.asAccept,
      this.isMultipleInputFiles,
      this.input.maxSelection,
    );
  }

  changeCloudInput(cDoc: CloudFileInfo | null) {
    let repoFolders = null;
    if (!cDoc) {
      // Add button clicked
      const selectedRepo = this.input.cloudRepos.find(
        (repo: any) =>
          repo.connector === this.selectedInputSource.value &&
          repo.region === this.selectedInputSource.region,
      );
      if (selectedRepo) {
        cDoc = selectedRepo;
        repoFolders = selectedRepo.files;
      } else {
        //login to the selected repo
        this.selectInputSource(this.selectedInputSource);
      }
    } else {
      repoFolders = this.input.cloudRepos.find(
        (repo: any) => repo.connector === cDoc?.connector,
      )?.files;
    }

    const selectedFolders = JSON.parse(JSON.stringify(repoFolders));
    const modalRef = this.modalService.open(MiddlewareBrowseModalComponent, {
      windowClass: 'xrx-modal',
      backdrop: 'static',
    });
    modalRef.componentInstance.returning = true;
    modalRef.componentInstance.enableFileTypes = this.input.fileTypes.asAccept;
    modalRef.componentInstance.multiSelect = this.isMultipleInputFiles;
    modalRef.componentInstance.maxSelection = this.input.maxSelection;
    modalRef.componentInstance.returningRepo = {
      connector: cDoc?.connector,
      region: cDoc?.region,
      accessToken: cDoc?.access,
      refreshToken: cDoc?.refresh,
      sitePath: cDoc?.sitePath,
      dir: cDoc?.dir,
      selectedFolders: this.isMultipleInputFiles ? selectedFolders : null,
    };

    modalRef.result
      .then((repoOptions) => {
        this.updateCloudRepoInputs(repoOptions);
      })
      .catch((ex) => {
        if (ex) {
          this.selectCloudRepo(this.cloudRepoOptions, true);
        }
      });
  }

  /**
   * enable input files from multiple cloud repos
   * @param repoOptions
   */
  updateCloudRepoInputs(repo: CloudRepo) {
    this.input.currentCloudRepo = repo;
    this.specialCharactersInCloudFileName = false;

    if (!this.input.cloudRepos || this.input.cloudRepos.length === 0) {
      this.input.cloudRepos = [];
    }

    if (this.input.supportMutibleInputSource) {
      let isNewRepo = true;
      for (let i = 0; i < this.input.cloudRepos.length; i++) {
        if (this.input.cloudRepos[i].connector === repo.connector) {
          // update the existing repo
          this.input.cloudRepos[i] = repo;
          isNewRepo = false;
          break;
        }
      }
      if (isNewRepo) {
        this.input.cloudRepos.push(repo);
      }
    } else {
      this.input.cloudRepos = [];
      this.input.cloudRepos.push(repo);
    }

    // Create CloudFileInfo object for each selected cloud file
    this.input.cloudRepoInputs = [];
    if (this.isMultipleInputFiles) {
      // process multiple repos -> multiple folders -> multiple files
      this.input.cloudRepos.forEach((repo: any) => {
        // handle multiple selected files. Instantiate a CloudRepoFileInfo object for each selected file.
        const cloudFolders = repo.files as MWSelectedFilesInFolder[];
        if (cloudFolders) {
          cloudFolders.forEach((folder) => {
            folder.files.forEach((file) => {
              const cloudFile: CloudFileInfo = Object.assign({}, repo);
              cloudFile.sitePath = folder.sitePath;
              cloudFile.dir = folder.dir;
              cloudFile.file = file;
              cloudFile.fileFormat = this.getFileFormat(file.name) ?? '';
              cloudFile.outputFileName = file.name.substr(
                0,
                file.name.lastIndexOf('.'),
              );
              cloudFile.insertPageNumber = 1;
              this.input.cloudRepoInputs.push(cloudFile);
            });
          });
        }
        // use the first file for default ouput file name creation
        // check 'file' in case no file is selected
        if (
          this.input.cloudRepoInputs.length &&
          this.input.cloudRepoInputs[0].file
        ) {
          this.handleSelectedFileFromCloud(
            this.input.cloudRepoInputs[0].file.name,
          );
        }
        // check for invalid characters in file names if multiple files have been selected
        if (this.input.cloudRepoInputs.length > 1) {
          for (let i = 0; i < this.input.cloudRepoInputs.length; i++) {
            if (
              !this.validateSpecialCharacters(
                this.input.cloudRepoInputs[i].file,
              )
            ) {
              this.specialCharactersInCloudFileName = true;
              break;
            }
          }
        }
      });
    }
    // single file from a cloud repo
    else {
      const cloudFile: CloudFileInfo = Object.assign(
        {},
        this.input.currentCloudRepo,
      );
      cloudFile.file = this.input.currentCloudRepo.files;
      // check 'file' in case no file is selected
      if (cloudFile.file) {
        const fileName = cloudFile.file.name;
        cloudFile.fileFormat = this.getFileFormat(fileName) ?? '';
        this.input.cloudRepoInputs.push(cloudFile);
        this.handleSelectedFileFromCloud(fileName);
      }
    }

    this.input.isBatchInput = this.input.cloudRepoInputs.length > 1;
    this.checkPageRange();
  }

  async validateSelectedCloudRepo(): Promise<boolean> {
    this.selectInputRepo = false;
    let validCloudRepo = false;

    if (!this.cloudRepoOptions) {
      return false;
    }

    try {
      //for saved workflow, with cloud output option, the value of (this.middlewareService.connectorToken could be the
      // one of input cloud repo, and be handled differently.
      if (
        this.middlewareService.connectorToken &&
        !this.cloudRepoOptions?.access
      ) {
        this.middlewareService.returningWebLogin(this.cloudRepoOptions);
        return true;
      }

      this.middlewareService.validateConnectorToken();
      await this.middlewareService.validatePath(this.cloudRepoOptions);
      validCloudRepo = true;
      //change output settings
      this.cloudRepoOptions.access = this.middlewareService.connectorToken;
      this.cloudRepoOptions.refresh =
        this.middlewareService.connectorRefreshToken;
    } catch (e: any) {
      if (e?.message == 'ACCESS_TOKEN_REQUIRED') {
        this.middlewareService.initiateWebLogin(this.cloudRepoOptions, true);
      } else {
        this.changeCloudFolder();
      }
    }
    return validCloudRepo;
  }

  onPaste(e: ClipboardEvent) {
    const pastedData = e.clipboardData?.getData('text');
    if (pastedData?.match(/^[1-9]\d*$/)) {
      return true;
    }
    return false;
  }

  onPageNumberChange() {
    if (this.input.insertPageNumber < 1) {
      this.input.insertPageNumber = 1;
      return false;
    }
    return this.validatePageNumber();
  }

  validatePageNumber() {
    const pageNumberInput = this.form.form.get('insertAfter');

    // reset error
    this.input.insertPageNumberError = '';

    if (this.input.insertPageNumber > this.input.maxInsertPageNumber) {
      setTimeout(() => {
        this.input.insertPageNumberError = 'SDE_INVALID_PAGE_NUMBER1';
        pageNumberInput?.setErrors({ customerror: true });

        return false;
      }, 100);
    }

    return true;
  }

  rangeOnKeyUp(event: KeyboardEvent) {
    // set range input as touched if it is untouched
    const rangeInput = this.form.form.get('pageRange');
    if (!rangeInput?.touched) {
      rangeInput?.markAsTouched();
    }

    this.isGoodToGo = true;
  }

  validateRange() {
    let pageRange = this.input.selectedPages,
      rangeInput = this.form.form.get('pageRange'),
      isValid = true;

    // if rangeInput is null or pageRange is empty or all is selected then return isValid is true
    if (
      rangeInput == null ||
      pageRange == '' ||
      this.pageRangeOption.value == 'SDE_ALL'
    ) {
      isValid = true;
      return isValid;
    }

    // reset error
    this.input.selectedPagesError = '';

    // remove any trailing commas
    if (this.input.selectedPages.endsWith(',')) {
      // remove the last char
      pageRange = this.input.selectedPages = this.input.selectedPages.slice(
        0,
        -1,
      );
    }

    // make sure that each 'group' is comma separated
    if (/\d+\-\d+? +\d+|\d+ +\d+/g.test(pageRange)) {
      // set error, set isValid to false & return
      this.setRangeError(rangeInput, 'SDE_INVALID_PAGE_RANGE1');
      isValid = false;
      return isValid;
    }

    // remove white space before further processing processing
    pageRange = pageRange.replace(/\s/g, '');

    if (pageRange == '0') {
      // set error, set isValid to false & return
      this.setRangeError(rangeInput, 'SDE_INVALID_PAGE_RANGE1');
      isValid = false;
      return isValid;
    }

    const rangeSplit = pageRange.split(',');
    for (let i = 0; i < rangeSplit.length; i++) {
      // make sure each range is valid (i.e is a number or a range of numbers separated by a dash)
      if (!rangeSplit[i].match(/^\d+$/) && !rangeSplit[i].match(/^\d+\-\d+$/)) {
        // set error,set isValid to false & break out of loop
        this.setRangeError(rangeInput, 'SDE_INVALID_PAGE_RANGE1');
        isValid = false;
        break;
        // make sure that in each range of numbers the first number in the range is less then the second number
      } else if (rangeSplit[i].match(/^\d+\-\d+$/)) {
        const dashPos = rangeSplit[i].indexOf('-'),
          v1 = parseInt(rangeSplit[i].substring(0, dashPos)),
          v2 = parseInt(rangeSplit[i].substring(dashPos + 1));
        if (v1 >= v2) {
          // set error,set isValid to false & break out of loop
          this.setRangeError(rangeInput, 'SDE_STARTING_PAGE_CANNOT');
          isValid = false;
          break;
          // make sure the neither of the numbers in the page range are zero or greater than 9999
        } else if (v1 == 0 || v2 == 0 || v1 > 9999 || v2 > 9999) {
          // set error,set isValid to false & break out of loop
          this.setRangeError(rangeInput, 'SDE_INVALID_PAGE_RANGE1');
          isValid = false;
          break;
        }
        // make sure that any single page number isn't 0 or greater than 9999
      } else if (rangeSplit[i].match(/^\d+$/)) {
        if (parseInt(rangeSplit[i]) == 0 || parseInt(rangeSplit[i]) > 9999) {
          // set error,set isValid to false & break out of loop
          this.setRangeError(rangeInput, 'SDE_INVALID_PAGE_RANGE1');
          isValid = false;
          break;
        }
      }
    }
    return isValid;
  }

  setRangeError(input: any, error: string) {
    setTimeout(() => {
      this.input.selectedPagesError = error;
      input.setErrors({ customerror: true });
    }, 100);
  }

  handleEnterKeyPress(event: Event) {
    // prevent <Enter> form submission if not a button
    if ((event.target as HTMLElement).tagName.toLowerCase() !== 'button') {
      return false;
    }
    return;
  }

  ngOnDestroy() {
    this.cloudRepoSub.unsubscribe();
    this.licenseErrorSub?.unsubscribe();
  }
}

export interface Step {
  index: number;
  name: string;
  buttonLabel: string;
}

export interface WorkflowModalResult {
  workflow: Workflow;
  inputFile: File | File[] | any;
  cloudRepoFiles: CloudFileInfo[];
  outputFileName?: string;
  selectedPages: string;
  translateOutputFormat?: string;
}
